// @ts-nocheck
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import * as cssLinter from "css";

// import SearchInput, { SearchInputStyles } from "@insite/content-library/Widgets/Header/SearchInput"; Starter Custom Code
import React from "react";
// Starter Custom Code
import SearchInput, { SearchInputStyles } from "@store/Overrides/Widgets/Header/SearchInput";

// Store Custom Code
import translate from "@insite/client-framework/Translate";

// Starter Custom Code - >
export const enum searchFields {
    categories = "categories",
    brands = "brands",
    contents = "contents",
    customCSS = "customCSS",
    // Store Custom Code - >
    turnOffEnterSearch = "turnOffEnterSearch",
    cardStyle = "cardStyle",
    useTabbedHeader = "useTabbedHeader",
    useLegacyAutocomplete = "useLegacyAutocomplete",
    loadProductsWhileTyping = "loadProductsWhileTyping",
    searchCommitBehavior = "searchCommitBehavior",
    // <-- Bunzl Custom Code
}

/* Store Custom Code - Start */
// Plain `as const` object + string-literal union (deliberately NOT a TS enum) for the
// search-commit interaction.
export const searchCommitBehaviors = {
    /** First commit loads products into the dropdown (hybrid view); a second commit navigates. */
    progressive: "progressive",
    /** Classic: the commit gesture navigates straight to the results page. */
    navigateImmediately: "navigateImmediately",
    /** Commit only loads products into the dropdown; navigation happens via the results link. */
    loadOnly: "loadOnly",
} as const;
export type SearchCommitBehavior = (typeof searchCommitBehaviors)[keyof typeof searchCommitBehaviors];
/* Store Custom Code - End */

interface OwnProps extends WidgetProps {
    fields: {
        [searchFields.categories]: boolean;
        [searchFields.brands]: boolean;
        [searchFields.customCSS]: string;
        [searchFields.contents]: boolean;
        // Store Custom Code
        [searchFields.useTabbedHeader]: boolean;

        [searchFields.turnOffEnterSearch]: boolean;
        [searchFields.cardStyle]: "elevated" | "outlined";
        [searchFields.useLegacyAutocomplete]: boolean;
        [searchFields.loadProductsWhileTyping]: boolean;
        [searchFields.searchCommitBehavior]: SearchCommitBehavior;
    };
}
// <-- Starter Custom Code

/* Starter Custom Code */
const defaultCustomCss = `.search-input{
}

.popover{
}

.autocomplete-column-wrapper{
}

.autocomplete-categories-header{
}

.autocomplete-categories-link{
}

.autocomplete-categories-note{
}

.autocomplete-brands-header{
}

.autocomplete-brands-link{
}

.autocomplete-brands-note{
}

.autocomplete-content-header{
}

.autocomplete-content-link{
}

.autocomplete-products-header{
}

.autocomplete-products-container{
}

.autocomplete-products-item{
}

.autocomplete-products-link{
}

.autocomplete-products-image{
}

.autocomplete-products-info{
}

.autocomplete-products-title{
}

.autocomplete-products-erp-number{
}`;
/* Starter Custom Code */

export interface HeaderSearchInputStyles {
    searchInputStyles?: SearchInputStyles;
}

export const searchInputStyles: HeaderSearchInputStyles = {};

// Starter Custom Code - Fields Added
export const HeaderSearchInput: React.FC<OwnProps & { isAnimated?: boolean }> = ({
    id,
    fields,
    isAnimated = false,
}) => {
    const styles = useMergeStyles("headerSearchInput", searchInputStyles);
    const isUsingTabbedHeader = fields[searchFields.useTabbedHeader];
    /* Store Custom Code - Start */
    const inputRef = React.useRef<HTMLInputElement>(null);
    return (
        <SearchInput
            id={id}
            isAnimated={isAnimated}
            inputRef={inputRef}
            extendedStyles={styles.searchInputStyles}
            searchable={fields}
        />
    );
    /* Store Custom Code - End */
};

const widgetModule: WidgetModule = {
    component: HeaderSearchInput,
    definition: {
        group: "Header",
        displayName: "Search Input",
        allowedContexts: ["Header"],
        // Starter Custom Code - >
        fieldDefinitions: [
            {
                name: searchFields.categories,
                displayName: "Enable Categories Search",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: searchFields.brands,
                displayName: "Enable Brands Search",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: searchFields.contents,
                displayName: "Enable Contents Search",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
            /* Store Custom Code - Start */
            {
                name: searchFields.useLegacyAutocomplete,
                displayName: translate("Use Legacy Autocomplete (relevancy off, autocomplete only)"),
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: searchFields.loadProductsWhileTyping,
                displayName: translate("[Testing] Load products while typing (hits product API every keystroke)"),
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 5,
            },
            {
                name: searchFields.searchCommitBehavior,
                displayName: translate("Search Commit Behavior"),
                editorTemplate: "DropDownField",
                defaultValue: searchCommitBehaviors.progressive,
                fieldType: "General",
                sortOrder: 6,
                options: [
                    {
                        displayName: "Progressive (load products, then navigate)",
                        value: searchCommitBehaviors.progressive,
                    },
                    { displayName: "Navigate immediately", value: searchCommitBehaviors.navigateImmediately },
                    { displayName: "Load only (results link navigates)", value: searchCommitBehaviors.loadOnly },
                ],
            },
            /* Store Custom Code - End */
            {
                name: searchFields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
            // Store Custom Code - >
            {
                name: searchFields.turnOffEnterSearch,
                displayName: translate("Turn Off Enter Search"),
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Contextual",
            },
            {
                name: searchFields.cardStyle,
                displayName: translate("Search Products Card Style"),
                editorTemplate: "DropDownField",
                defaultValue: "elevated",
                fieldType: "Contextual",
                options: [
                    { displayName: "Elevated", value: "elevated" },
                    { displayName: "Outlined", value: "outlined" },
                ],
            },
            {
                name: searchFields.useTabbedHeader,
                displayName: translate("Use Tabbed Header"),
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Contextual",
            },
            // <-- Bunzl Custom Code
        ],
        // <-- Starter Custom Code
    },
};

export default widgetModule;
